import { useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Button, Divider, Stack, Typography } from "@mui/material";
import EditIcon from "@mui/icons-material/Edit";
import { Field } from "../components/miProfile/Field";
import { useForm } from "../../hooks";
import { startUpdateUser } from "../../store/auth/thunks";
import { useTranslation } from "react-i18next";
import { CheckboxInput } from "../components/BooleanInput";

export const MyProfile = ({ user }) => {
  const dispatch = useDispatch();
  const { t } = useTranslation("global");
  const [isDisabled, setIsDisabled] = useState(true);
  const { role } = useSelector((state) => state.auth);
  const [formValues, handleInputChange, reset] = useForm(user);

  const {
    country,
    name,
    surnames,
    email,
    phone,
    address,
    zipCode,
    swift,
    iban,
    dni,
    linkedin,
    visibleProfile
  } = formValues;

  const handleEdit = () => {
    setIsDisabled(false);
  };

  const handleCancel = () => {
    reset();
    setIsDisabled(true);
  };

  const handleConfirm = async (e) => {
    e.preventDefault();
    dispatch(startUpdateUser(formValues, handleCancel, setIsDisabled, t));
  };

  return (
    <>
      <Stack direction="row" justifyContent="space-between" alignItems="center">
        <Typography color="primary" variant="h5">
          {t("myprofile.title")}
        </Typography>
        <Button
          onClick={handleEdit}
          variant="outlined"
          color="secondary"
          endIcon={<EditIcon />}
        >
          {t("myprofile.editButton")}
        </Button>
      </Stack>
      <Divider sx={{ marginTop: 2, marginBottom: 3 }} />

      <form onSubmit={handleConfirm}>
        <Stack minHeight="calc(100vh - 290px)" gap={2}>
          <Stack direction="row" width="100%" gap={2}>
            <Field
              translation="myprofile"
              name={name}
              isDisabled={isDisabled}
              title="name"
              handleInputChange={handleInputChange}
            />
            <Field
              translation="myprofile"
              name={surnames}
              isDisabled={isDisabled}
              title="surnames"
              handleInputChange={handleInputChange}
            />
          </Stack>

          <Stack direction="row" width="100%" gap={2}>
            <Field
              translation="myprofile"
              name={email}
              title="email"
              handleInputChange={handleInputChange}
              required={true}
              small={true}
              isDisabled={isDisabled}
            />
            <Field
              translation="myprofile"
              name={dni}
              title="dni"
              handleInputChange={handleInputChange}
              small={true}
              isDisabled={isDisabled}
            />
            <Field
              translation="myprofile"
              name={phone}
              title="phone"
              handleInputChange={handleInputChange}
              small={true}
              isDisabled={isDisabled}
            />
          </Stack>
          <Stack direction="row" width="100%" gap={2}>
            <Field
              translation="myprofile"
              name={address}
              isDisabled={isDisabled}
              title="address"
              handleInputChange={handleInputChange}
            />
            <Field
              translation="myprofile"
              name={linkedin}
              isDisabled={isDisabled}
              title="linkedin"
              handleInputChange={handleInputChange}
            />
          </Stack>

          <Stack direction="row" width="100%" gap={2}>
            <Field
              translation="myprofile"
              name={country}
              title="country"
              handleInputChange={handleInputChange}
              isDisabled={isDisabled}
            />
            <Field
              translation="myprofile"
              name={zipCode}
              title="zipCode"
              handleInputChange={handleInputChange}
              isDisabled={isDisabled}
            />
          </Stack>

          <Stack direction="row" width="100%" gap={2}>
            <Field
              translation="myprofile"
              name={swift}
              title="swift"
              handleInputChange={handleInputChange}
              isDisabled={role !== "Admin" || isDisabled}
            />
            <Field
              translation="myprofile"
              name={iban}
              title="iban"
              handleInputChange={handleInputChange}
              isDisabled={role !== "Admin" || isDisabled}
            />
          </Stack>

          <Stack direction="row" width="100%" gap={2}>
            <CheckboxInput 
              translation="myprofile"
              value={visibleProfile}
              title="visibleProfile"
              handleInputChange={handleInputChange}
              isDisabled={role !== "Admin" || isDisabled}
            />
          </Stack>

          {!isDisabled && (
            <Stack direction="row" justifyContent="end" gap={2} marginTop={3}>
              <Button
                sx={{ width: "140px" }}
                color="secondary"
                onClick={handleCancel}
                variant="outlined"
              >
                {t("myprofile.cancelButton")}
              </Button>
              <Button sx={{ width: "140px" }} type="submit" variant="contained">
                {t("myprofile.confirmButton")}
              </Button>
            </Stack>
          )}
        </Stack>
      </form>
    </>
  );
};
